'use client'

import * as React from 'react'
import { X } from 'lucide-react'
import { NewsletterCapture } from '@/components/marketing/newsletter-capture'
import { canShow, markShown } from '@/lib/growth/frequency'
import { track } from '@/lib/analytics/ga4'

// Scroll-triggered benchmark card for statistics/benchmark articles.
// Slides in bottom-right once the reader passes 55% depth (they're engaged,
// not landing). Offers the full benchmark tables by email via the existing
// NewsletterCapture (Turnstile + source attribution included).
// Frequency: one impression per visitor per 7 days, shared cap with the
// other rails via lib/growth/frequency.

const CAP_KEY = 'benchmark-rail'

export function BenchmarkRail({ slug }: { slug: string }) {
  const [open, setOpen] = React.useState(false)
  const fired = React.useRef(false)

  React.useEffect(() => {
    function onScroll() {
      if (fired.current) return
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight
      if (depth < 0.55) return
      fired.current = true
      if (!canShow(CAP_KEY)) return
      markShown(CAP_KEY)
      setOpen(true)
      track('growth_benchmark_rail_shown', { surface: `/blog/${slug}` })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [slug])

  if (!open) return null

  return (
    <aside
      aria-label="Get the full benchmark tables by email"
      className="fixed bottom-4 right-4 z-[60] w-[min(24rem,calc(100vw-2rem))] rounded-2xl border bg-card p-5 shadow-xl motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-300"
    >
      <button
        aria-label="Dismiss"
        onClick={() => {
          setOpen(false)
          track('growth_benchmark_rail_dismissed', { surface: `/blog/${slug}` })
        }}
        className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="h-4 w-4" />
      </button>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">The numbers behind this piece</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground text-pretty">
        Want the full benchmark tables in one place? One email, plus each new report as it lands.
        Nothing else.
      </p>
      <div className="mt-3">
        <NewsletterCapture source={`/blog/${slug}#benchmark-rail`} compact cta="Send me the tables" />
      </div>
    </aside>
  )
}
